import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Zap, Shield, Clock, Star, Trophy } from "lucide-react";

const RobotJumpGame = ({ sessionToken, onGameOver, playerName }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const gameLoopRef = useRef(null);
  const gameStateRef = useRef(null);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [aiBoostStatus, setAiBoostStatus] = useState("READY");
  const [combo, setCombo] = useState(0);
  const [showCombo, setShowCombo] = useState(false);
  const [activePowerUps, setActivePowerUps] = useState([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [canvasDimensions, setCanvasDimensions] = useState({
    width: 1152,
    height: 500,
  });

  useEffect(() => {
    const updateCanvasSize = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.clientWidth;
      const maxWidth = 1152;
      const maxHeight = 500;
      const aspectRatio = maxWidth / maxHeight;

      let width = Math.min(containerWidth, maxWidth);
      let height = width / aspectRatio;

      // For very small screens, ensure minimum playable size
      if (width < 320) {
        width = 320;
        height = width / aspectRatio;
      }

      setCanvasDimensions({ width, height });
    };

    updateCanvasSize();
    window.addEventListener("resize", updateCanvasSize);
    return () => window.removeEventListener("resize", updateCanvasSize);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    // Set canvas size with better resolution
    const dpr = window.devicePixelRatio || 1;
    const displayWidth = canvasDimensions.width;
    const displayHeight = canvasDimensions.height;

    canvas.width = displayWidth * dpr;
    canvas.height = displayHeight * dpr;
    canvas.style.width = `${displayWidth}px`;
    canvas.style.height = `${displayHeight}px`;
    ctx.scale(dpr, dpr);

    const CANVAS_WIDTH = displayWidth;
    const CANVAS_HEIGHT = displayHeight;
    const scaleFactor = displayWidth / 1152;

    // Enhanced Particle system
    class Particle {
      constructor(x, y, color, type = "normal") {
        this.x = x;
        this.y = y;
        this.vx = (Math.random() - 0.5) * 8 * scaleFactor;
        this.vy = (Math.random() - 0.5) * 8 * scaleFactor - 2;
        this.life = 1;
        this.color = color;
        this.size = (Math.random() * 5 + 3) * scaleFactor;
        this.type = type;
        this.rotation = Math.random() * Math.PI * 2;
        this.rotationSpeed = (Math.random() - 0.5) * 0.2;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.vy += 0.3 * scaleFactor;
        this.vx *= 0.98;
        this.life -= 0.015;
        this.size *= 0.96;
        this.rotation += this.rotationSpeed;
      }

      draw(ctx) {
        ctx.save();
        ctx.globalAlpha = this.life;
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);

        if (this.type === "star") {
          this.drawStar(ctx);
        } else if (this.type === "spark") {
          ctx.fillStyle = this.color;
          ctx.shadowBlur = 10 * scaleFactor;
          ctx.shadowColor = this.color;
          ctx.fillRect(-this.size / 2, -this.size / 2, this.size, this.size);
        } else if (this.type === "circle") {
          const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, this.size);
          gradient.addColorStop(0, this.color);
          gradient.addColorStop(1, this.color + "00");
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(0, 0, this.size, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillStyle = this.color;
          ctx.shadowBlur = 8 * scaleFactor;
          ctx.shadowColor = this.color;
          ctx.beginPath();
          ctx.arc(0, 0, this.size, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }

      drawStar(ctx) {
        ctx.fillStyle = this.color;
        ctx.shadowBlur = 15 * scaleFactor;
        ctx.shadowColor = this.color;
        ctx.beginPath();
        for (let i = 0; i < 5; i++) {
          const angle = (i * 4 * Math.PI) / 5 - Math.PI / 2;
          const x = Math.cos(angle) * this.size;
          const y = Math.sin(angle) * this.size;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.fill();
      }
    }

    // Trail effect for player
    class Trail {
      constructor(x, y, color) {
        this.x = x;
        this.y = y;
        this.life = 1;
        this.color = color;
        this.size = 50 * scaleFactor;
      }

      update() {
        this.life -= 0.05;
        this.size *= 0.95;
      }

      draw(ctx) {
        ctx.save();
        ctx.globalAlpha = this.life * 0.3;
        const gradient = ctx.createRadialGradient(
          this.x,
          this.y,
          0,
          this.x,
          this.y,
          this.size
        );
        gradient.addColorStop(0, this.color);
        gradient.addColorStop(1, this.color + "00");
        ctx.fillStyle = gradient;
        ctx.fillRect(
          this.x - this.size,
          this.y - this.size,
          this.size * 2,
          this.size * 2
        );
        ctx.restore();
      }
    }

    // Game state
    const game = {
      running: false,
      player: {
        x: 120 * scaleFactor,
        y: (CANVAS_HEIGHT - 120) * 0.9,
        width: 100 * scaleFactor,
        height: 100 * scaleFactor,
        dy: 0,
        gravity: 0.9 * scaleFactor,
        jumpPower: -17 * scaleFactor,
        superJumpPower: -25 * scaleFactor,
        isJumping: false,
        rotation: 0,
        targetRotation: 0,
        squish: 1,
        trail: [],
      },
      obstacles: [],
      powerUps: [],
      coins: [],
      particles: [],
      score: 0,
      combo: 0,
      gameSpeed: 6 * scaleFactor,
      aiBoostActive: false,
      aiBoostCooldown: false,
      frameCount: 0,
      startTime: null,
      robotImage: new Image(),
      logoImages: [],
      imagesLoaded: false,
      backgroundOffset: 0,
      clouds: [],
      invincible: false,
      slowMotion: false,
      doublePoints: false,
      groundY: (CANVAS_HEIGHT - 70) * 0.95,
    };

    // Initialize clouds with better distribution
    for (let i = 0; i < 8; i++) {
      game.clouds.push({
        x: Math.random() * CANVAS_WIDTH,
        y: Math.random() * (CANVAS_HEIGHT * 0.5) + 50 * scaleFactor,
        speed: (Math.random() * 0.8 + 0.3) * scaleFactor,
        size: (Math.random() * 50 + 40) * scaleFactor,
        opacity: Math.random() * 0.3 + 0.2,
      });
    }

    // Load images
    game.robotImage.src = "/robot.png";
    const logoSources = [
      "/logo1.png",
      "/Wizzyl.png",
      "/main-logo.svg",
      "/logo3.png",
      "/logo2.png",
    ];

    let loadedCount = 0;
    const totalImages = logoSources.length + 1;

    function checkAllImagesLoaded() {
      loadedCount++;
      if (loadedCount === totalImages) {
        game.imagesLoaded = true;
        setImagesLoaded(true);
      }
    }

    game.robotImage.onload = checkAllImagesLoaded;
    game.robotImage.onerror = checkAllImagesLoaded;

    logoSources.forEach((src) => {
      const img = new Image();
      img.src = src;
      img.onload = checkAllImagesLoaded;
      img.onerror = checkAllImagesLoaded;
      game.logoImages.push(img);
    });

    gameStateRef.current = game;

    // Enhanced draw functions
    function drawPlayer() {
      const player = game.player;

      // Draw trail
      for (let i = player.trail.length - 1; i >= 0; i--) {
        const trail = player.trail[i];
        trail.update();
        trail.draw(ctx);
        if (trail.life <= 0) player.trail.splice(i, 1);
      }

      // Add trail particles when moving fast
      if (
        game.frameCount % 3 === 0 &&
        (game.aiBoostActive || player.isJumping)
      ) {
        const color = game.aiBoostActive ? "#ff6b6b" : "#667eea";
        player.trail.push(
          new Trail(
            player.x + player.width / 2,
            player.y + player.height / 2,
            color
          )
        );
      }

      ctx.save();
      ctx.translate(player.x + player.width / 2, player.y + player.height / 2);
      ctx.rotate(player.rotation);
      ctx.scale(1, player.squish);

      // Enhanced glow effects
      if (game.aiBoostActive) {
        ctx.shadowBlur = 40 * scaleFactor;
        ctx.shadowColor = "#ff6b6b";

        // Pulsing outer glow
        const pulse =
          Math.sin(game.frameCount * 0.2) * 10 * scaleFactor + 20 * scaleFactor;
        const glowGradient = ctx.createRadialGradient(
          0,
          0,
          player.width / 3,
          0,
          0,
          player.width + pulse
        );
        glowGradient.addColorStop(0, "rgba(255, 107, 107, 0)");
        glowGradient.addColorStop(0.5, "rgba(255, 107, 107, 0.4)");
        glowGradient.addColorStop(1, "rgba(255, 107, 107, 0)");
        ctx.fillStyle = glowGradient;
        ctx.fillRect(
          -player.width * 1.5,
          -player.height * 1.5,
          player.width * 3,
          player.height * 3
        );
      }

      if (game.invincible) {
        ctx.shadowBlur = 30 * scaleFactor;
        ctx.shadowColor = "#FFD700";

        // Rotating shield
        ctx.save();
        ctx.rotate(game.frameCount * 0.05);
        ctx.strokeStyle = "rgba(255, 215, 0, 0.6)";
        ctx.lineWidth = 4 * scaleFactor;
        for (let i = 0; i < 6; i++) {
          ctx.beginPath();
          ctx.arc(
            0,
            0,
            player.width / 2 +
              15 * scaleFactor +
              Math.sin(game.frameCount * 0.1 + i) * 3 * scaleFactor,
            (i * Math.PI) / 3,
            (i * Math.PI) / 3 + Math.PI / 6
          );
          ctx.stroke();
        }
        ctx.restore();
      }

      // Draw robot with better fallback
      if (game.robotImage.complete && game.robotImage.naturalHeight !== 0) {
        ctx.shadowBlur = 0;
        ctx.drawImage(
          game.robotImage,
          -player.width / 2,
          -player.height / 2,
          player.width,
          player.height
        );
      } else {
        // Modern robot design
        const gradient = ctx.createLinearGradient(
          -player.width / 2,
          -player.height / 2,
          player.width / 2,
          player.height / 2
        );
        gradient.addColorStop(0, "#667eea");
        gradient.addColorStop(0.5, "#764ba2");
        gradient.addColorStop(1, "#f093fb");

        // Robot body
        ctx.fillStyle = gradient;
        ctx.shadowBlur = 15 * scaleFactor;
        ctx.shadowColor = "rgba(102, 126, 234, 0.5)";

        const radius = 8 * scaleFactor;
        ctx.beginPath();
        ctx.moveTo(-player.width / 2 + radius, -player.height / 2);
        ctx.lineTo(player.width / 2 - radius, -player.height / 2);
        ctx.quadraticCurveTo(
          player.width / 2,
          -player.height / 2,
          player.width / 2,
          -player.height / 2 + radius
        );
        ctx.lineTo(player.width / 2, player.height / 2 - radius);
        ctx.quadraticCurveTo(
          player.width / 2,
          player.height / 2,
          player.width / 2 - radius,
          player.height / 2
        );
        ctx.lineTo(-player.width / 2 + radius, player.height / 2);
        ctx.quadraticCurveTo(
          -player.width / 2,
          player.height / 2,
          -player.width / 2,
          player.height / 2 - radius
        );
        ctx.lineTo(-player.width / 2, -player.height / 2 + radius);
        ctx.quadraticCurveTo(
          -player.width / 2,
          -player.height / 2,
          -player.width / 2 + radius,
          -player.height / 2
        );
        ctx.closePath();
        ctx.fill();

        // Highlights
        ctx.shadowBlur = 0;
        const shine = ctx.createLinearGradient(
          -player.width / 2,
          -player.height / 2,
          0,
          0
        );
        shine.addColorStop(0, "rgba(255, 255, 255, 0.6)");
        shine.addColorStop(1, "rgba(255, 255, 255, 0)");
        ctx.fillStyle = shine;
        ctx.fillRect(
          -player.width / 2,
          -player.height / 2,
          player.width / 2,
          player.height / 2
        );

        // Animated visor
        const eyeY =
          -8 * scaleFactor + Math.sin(game.frameCount * 0.08) * 2 * scaleFactor;
        ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
        ctx.fillRect(
          -20 * scaleFactor,
          eyeY - 8 * scaleFactor,
          40 * scaleFactor,
          12 * scaleFactor
        );

        // Glowing eye line
        const eyeGradient = ctx.createLinearGradient(
          -18 * scaleFactor,
          eyeY,
          18 * scaleFactor,
          eyeY
        );
        eyeGradient.addColorStop(0, "#00ffff00");
        eyeGradient.addColorStop(0.5, "#00ffff");
        eyeGradient.addColorStop(1, "#00ffff00");
        ctx.fillStyle = eyeGradient;
        ctx.fillRect(
          -18 * scaleFactor,
          eyeY - 2 * scaleFactor,
          36 * scaleFactor,
          4 * scaleFactor
        );

        // Antenna with glow
        ctx.strokeStyle = "#ff6b6b";
        ctx.lineWidth = 3 * scaleFactor;
        ctx.shadowBlur = 10 * scaleFactor;
        ctx.shadowColor = "#ff6b6b";
        ctx.beginPath();
        ctx.moveTo(0, -player.height / 2);
        ctx.lineTo(0, -player.height / 2 - 18 * scaleFactor);
        ctx.stroke();

        ctx.fillStyle = "#ff6b6b";
        ctx.beginPath();
        ctx.arc(
          0,
          -player.height / 2 - 18 * scaleFactor,
          5 * scaleFactor,
          0,
          Math.PI * 2
        );
        ctx.fill();

        // Pulsing light on antenna
        ctx.fillStyle = `rgba(255, 107, 107, ${
          Math.sin(game.frameCount * 0.2) * 0.5 + 0.5
        })`;
        ctx.beginPath();
        ctx.arc(
          0,
          -player.height / 2 - 18 * scaleFactor,
          7 * scaleFactor,
          0,
          Math.PI * 2
        );
        ctx.fill();
      }

      // Enhanced boost flames
      if (game.aiBoostActive) {
        ctx.shadowBlur = 20 * scaleFactor;
        for (let i = 0; i < 4; i++) {
          const flameSize =
            Math.sin(game.frameCount * 0.3 + i) * 8 * scaleFactor +
            20 * scaleFactor;
          const colors = ["#ff6b6b", "#ff8787", "#ffa5a5", "#ffcccc"];
          ctx.fillStyle = colors[i];
          ctx.shadowColor = colors[i];
          ctx.beginPath();
          ctx.moveTo(
            -player.width / 2,
            -10 * scaleFactor + i * 8 * scaleFactor
          );
          ctx.quadraticCurveTo(
            -player.width / 2 - flameSize - i * 5 * scaleFactor,
            i * 8 * scaleFactor,
            -player.width / 2,
            10 * scaleFactor + i * 8 * scaleFactor
          );
          ctx.fill();
        }
      }

      ctx.restore();

      // Floating combo text
      if (game.combo > 1) {
        ctx.save();
        const bounceY = Math.sin(game.frameCount * 0.15) * 8 * scaleFactor;
        const scale = 1 + Math.sin(game.frameCount * 0.2) * 0.1;

        ctx.translate(
          player.x + player.width / 2,
          player.y - 40 * scaleFactor + bounceY
        );
        ctx.scale(scale, scale);

        ctx.shadowBlur = 15 * scaleFactor;
        ctx.shadowColor = "#FFD700";
        ctx.fillStyle = "#FFD700";
        ctx.font = `bold ${28 * scaleFactor}px Arial`;
        ctx.textAlign = "center";
        ctx.fillText(`${game.combo}x`, 0, 0);

        ctx.font = `bold ${14 * scaleFactor}px Arial`;
        ctx.fillStyle = "#FFA500";
        ctx.fillText("COMBO", 0, 18 * scaleFactor);
        ctx.restore();
      }
    }

    function drawObstacle(obs) {
      ctx.save();

      const logoImg = game.logoImages[obs.logoIndex];
      const pulse = Math.sin(game.frameCount * 0.1) * 2 * scaleFactor;

      // Enhanced shadow
      ctx.fillStyle = "rgba(0, 0, 0, 0.2)";
      ctx.beginPath();
      ctx.ellipse(
        obs.x + obs.width / 2,
        game.groundY + 10 * scaleFactor,
        obs.width / 2 + 5 * scaleFactor,
        8 * scaleFactor,
        0,
        0,
        Math.PI * 2
      );
      ctx.fill();

      // Animated glow
      if (obs.x < CANVAS_WIDTH / 2) {
        ctx.shadowBlur = (20 + pulse) * scaleFactor;
        ctx.shadowColor = "rgba(118, 75, 162, 0.5)";
      }

      if (logoImg && logoImg.complete && logoImg.naturalHeight !== 0) {
        // Clean white background with gradient border
        const bgGradient = ctx.createLinearGradient(
          obs.x,
          obs.y,
          obs.x,
          obs.y + obs.height
        );
        bgGradient.addColorStop(0, "#ffffff");
        bgGradient.addColorStop(1, "#f8f9fa");
        ctx.fillStyle = bgGradient;

        const radius = 12 * scaleFactor;
        ctx.beginPath();
        ctx.moveTo(obs.x + radius, obs.y);
        ctx.lineTo(obs.x + obs.width - radius, obs.y);
        ctx.quadraticCurveTo(
          obs.x + obs.width,
          obs.y,
          obs.x + obs.width,
          obs.y + radius
        );
        ctx.lineTo(obs.x + obs.width, obs.y + obs.height - radius);
        ctx.quadraticCurveTo(
          obs.x + obs.width,
          obs.y + obs.height,
          obs.x + obs.width - radius,
          obs.y + obs.height
        );
        ctx.lineTo(obs.x + radius, obs.y + obs.height);
        ctx.quadraticCurveTo(
          obs.x,
          obs.y + obs.height,
          obs.x,
          obs.y + obs.height - radius
        );
        ctx.lineTo(obs.x, obs.y + radius);
        ctx.quadraticCurveTo(obs.x, obs.y, obs.x + radius, obs.y);
        ctx.closePath();
        ctx.fill();

        // Gradient border
        const borderGradient = ctx.createLinearGradient(
          obs.x,
          obs.y,
          obs.x,
          obs.y + obs.height
        );
        borderGradient.addColorStop(0, "#667eea");
        borderGradient.addColorStop(0.5, "#764ba2");
        borderGradient.addColorStop(1, "#667eea");
        ctx.strokeStyle = borderGradient;
        ctx.lineWidth = 4 * scaleFactor;
        ctx.stroke();

        // Draw logo with optimized sizing
        const aspectRatio = logoImg.width / logoImg.height;
        const padding = 16 * scaleFactor;
        let drawWidth = obs.width - padding;
        let drawHeight = obs.height - padding;

        // Calculate dimensions to maintain aspect ratio while maximizing size
        if (aspectRatio > 1) {
          // Logo is wider than tall
          drawHeight = drawWidth / aspectRatio;
          if (drawHeight > obs.height - padding) {
            drawHeight = obs.height - padding;
            drawWidth = drawHeight * aspectRatio;
          }
        } else {
          // Logo is taller than wide or square
          drawWidth = drawHeight * aspectRatio;
          if (drawWidth > obs.width - padding) {
            drawWidth = obs.width - padding;
            drawHeight = drawWidth / aspectRatio;
          }
        }

        // Ensure minimum size for visibility
        const minSize = 30 * scaleFactor;
        if (drawWidth < minSize || drawHeight < minSize) {
          if (aspectRatio > 1) {
            drawWidth = minSize;
            drawHeight = minSize / aspectRatio;
          } else {
            drawHeight = minSize;
            drawWidth = minSize * aspectRatio;
          }
        }

        const offsetX = (obs.width - drawWidth) / 2;
        const offsetY = (obs.height - drawHeight) / 2;

        ctx.drawImage(
          logoImg,
          obs.x + offsetX,
          obs.y + offsetY,
          drawWidth,
          drawHeight
        );
      } else {
        // Modern fallback design
        const gradient = ctx.createLinearGradient(
          obs.x,
          obs.y,
          obs.x,
          obs.y + obs.height
        );
        gradient.addColorStop(0, "#764ba2");
        gradient.addColorStop(0.5, "#667eea");
        gradient.addColorStop(1, "#5a67d8");

        const radius = 12 * scaleFactor;
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(obs.x + radius, obs.y);
        ctx.lineTo(obs.x + obs.width - radius, obs.y);
        ctx.quadraticCurveTo(
          obs.x + obs.width,
          obs.y,
          obs.x + obs.width,
          obs.y + radius
        );
        ctx.lineTo(obs.x + obs.width, obs.y + obs.height - radius);
        ctx.quadraticCurveTo(
          obs.x + obs.width,
          obs.y + obs.height,
          obs.x + obs.width - radius,
          obs.y + obs.height
        );
        ctx.lineTo(obs.x + radius, obs.y + obs.height);
        ctx.quadraticCurveTo(
          obs.x,
          obs.y + obs.height,
          obs.x,
          obs.y + obs.height - radius
        );
        ctx.lineTo(obs.x, obs.y + radius);
        ctx.quadraticCurveTo(obs.x, obs.y, obs.x + radius, obs.y);
        ctx.closePath();
        ctx.fill();

        // Danger stripes with animation
        ctx.save();
        ctx.clip();
        ctx.strokeStyle = "rgba(255, 255, 255, 0.2)";
        ctx.lineWidth = 3 * scaleFactor;
        const offset = (game.frameCount * 3 * scaleFactor) % (30 * scaleFactor);
        for (
          let i = -30 * scaleFactor;
          i < obs.height + 30 * scaleFactor;
          i += 15 * scaleFactor
        ) {
          ctx.beginPath();
          ctx.moveTo(obs.x, obs.y + i + offset);
          ctx.lineTo(obs.x + obs.width, obs.y + i + offset - 20 * scaleFactor);
          ctx.stroke();
        }
        ctx.restore();

        // Warning icon
        ctx.fillStyle = "#ff6b6b";
        ctx.font = `bold ${30 * scaleFactor}px Arial`;
        ctx.textAlign = "center";
        ctx.shadowBlur = 10 * scaleFactor;
        ctx.shadowColor = "#ff6b6b";
        ctx.fillText(
          "⚠",
          obs.x + obs.width / 2,
          obs.y + obs.height / 2 + 10 * scaleFactor
        );
      }

      ctx.restore();
    }

    function drawCoin(coin) {
      ctx.save();

      const rotation = (game.frameCount * 0.12) % (Math.PI * 2);
      const scale = Math.abs(Math.cos(rotation)) * 0.8 + 0.2;
      const bounce = Math.sin(game.frameCount * 0.1 + coin.x) * 5 * scaleFactor;

      ctx.translate(coin.x + coin.size / 2, coin.y + coin.size / 2 + bounce);
      ctx.scale(scale, 1);

      // Outer glow
      const outerGlow = ctx.createRadialGradient(0, 0, 0, 0, 0, coin.size);
      outerGlow.addColorStop(0, "rgba(255, 215, 0, 0)");
      outerGlow.addColorStop(0.7, "rgba(255, 215, 0, 0.3)");
      outerGlow.addColorStop(1, "rgba(255, 215, 0, 0)");
      ctx.fillStyle = outerGlow;
      ctx.beginPath();
      ctx.arc(0, 0, coin.size * 1.5, 0, Math.PI * 2);
      ctx.fill();

      // Coin body with gradient
      const gradient = ctx.createRadialGradient(
        0,
        -5 * scaleFactor,
        0,
        0,
        0,
        coin.size / 2
      );
      gradient.addColorStop(0, "#FFD700");
      gradient.addColorStop(0.5, "#FFA500");
      gradient.addColorStop(1, "#FF8C00");

      ctx.fillStyle = gradient;
      ctx.shadowBlur = 20 * scaleFactor;
      ctx.shadowColor = "#FFD700";
      ctx.beginPath();
      ctx.arc(0, 0, coin.size / 2, 0, Math.PI * 2);
      ctx.fill();

      // Inner highlight
      ctx.fillStyle = "rgba(255, 255, 255, 0.4)";
      ctx.beginPath();
      ctx.arc(
        -3 * scaleFactor,
        -3 * scaleFactor,
        coin.size / 4,
        0,
        Math.PI * 2
      );
      ctx.fill();

      // Dollar sign
      ctx.shadowBlur = 5 * scaleFactor;
      ctx.shadowColor = "#000";
      ctx.fillStyle = "#FFF";
      ctx.font = `bold ${18 * scaleFactor}px Arial`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("$", 0, 1 * scaleFactor);

      ctx.restore();
    }

    function drawPowerUp(powerUp) {
      ctx.save();

      const bounce = Math.sin(game.frameCount * 0.12) * 8 * scaleFactor;
      const pulse = Math.sin(game.frameCount * 0.2) * 0.15 + 1;

      ctx.translate(
        powerUp.x + powerUp.size / 2,
        powerUp.y + powerUp.size / 2 + bounce
      );
      ctx.scale(pulse, pulse);

      // Outer rings
      for (let i = 3; i > 0; i--) {
        ctx.strokeStyle =
          powerUp.color + Math.floor((i / 3) * 100).toString(16);
        ctx.lineWidth = 3 * scaleFactor;
        ctx.shadowBlur = 15 * scaleFactor;
        ctx.shadowColor = powerUp.color;
        ctx.beginPath();
        ctx.arc(0, 0, powerUp.size / 2 + i * 6 * scaleFactor, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Power-up body
      const gradient = ctx.createRadialGradient(
        0,
        -5 * scaleFactor,
        0,
        0,
        0,
        powerUp.size / 2
      );
      gradient.addColorStop(0, powerUp.color);
      gradient.addColorStop(0.7, powerUp.color + "CC");
      gradient.addColorStop(1, powerUp.color + "66");

      ctx.fillStyle = gradient;
      ctx.shadowBlur = 25 * scaleFactor;
      ctx.shadowColor = powerUp.color;
      ctx.beginPath();
      ctx.arc(0, 0, powerUp.size / 2, 0, Math.PI * 2);
      ctx.fill();

      // Highlight
      ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
      ctx.beginPath();
      ctx.arc(
        -5 * scaleFactor,
        -5 * scaleFactor,
        powerUp.size / 4,
        0,
        Math.PI * 2
      );
      ctx.fill();

      // Icon
      ctx.shadowBlur = 0;
      ctx.fillStyle = "white";
      ctx.font = `bold ${24 * scaleFactor}px Arial`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(powerUp.icon, 0, 1 * scaleFactor);

      ctx.restore();
    }

    function drawGround() {
      const groundY = game.groundY;

      // Ground with gradient
      const gradient = ctx.createLinearGradient(0, groundY, 0, CANVAS_HEIGHT);
      gradient.addColorStop(0, "#4CAF50");
      gradient.addColorStop(0.3, "#45a049");
      gradient.addColorStop(1, "#388E3C");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, groundY, CANVAS_WIDTH, CANVAS_HEIGHT - groundY);

      // Grass line
      ctx.strokeStyle = "#66BB6A";
      ctx.lineWidth = 3 * scaleFactor;
      ctx.beginPath();
      ctx.moveTo(0, groundY);
      ctx.lineTo(CANVAS_WIDTH, groundY);
      ctx.stroke();

      // Animated grass blades
      ctx.strokeStyle = "#5cb85c";
      ctx.lineWidth = 2 * scaleFactor;
      const offset = (game.backgroundOffset * 3) % (40 * scaleFactor);
      for (
        let i = -40 * scaleFactor;
        i < CANVAS_WIDTH + 40 * scaleFactor;
        i += 40 * scaleFactor
      ) {
        const grassHeight = (15 + Math.sin(i * 0.1) * 5) * scaleFactor;
        ctx.beginPath();
        ctx.moveTo(i + offset, groundY);
        ctx.quadraticCurveTo(
          i + offset + 5 * scaleFactor,
          groundY - grassHeight,
          i + offset + 10 * scaleFactor,
          groundY - grassHeight / 2
        );
        ctx.stroke();
      }

      // Ground details
      ctx.fillStyle = "rgba(255, 255, 255, 0.15)";
      for (let i = 0; i < CANVAS_WIDTH; i += 100 * scaleFactor) {
        const x = i + ((offset * 2) % (100 * scaleFactor));
        ctx.fillRect(
          x,
          groundY + 25 * scaleFactor,
          30 * scaleFactor,
          4 * scaleFactor
        );
        ctx.fillRect(
          x + 50 * scaleFactor,
          groundY + 45 * scaleFactor,
          20 * scaleFactor,
          3 * scaleFactor
        );
      }
    }

    function drawBackground() {
      // Dynamic sky with time-based color
      const timeProgress = (game.frameCount / 2000) % 1;
      const hue = 200 + timeProgress * 40;
      const lightness = 70 + Math.sin(timeProgress * Math.PI) * 15;

      const gradient = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
      gradient.addColorStop(0, `hsl(${hue}, 60%, ${lightness}%)`);
      gradient.addColorStop(0.6, `hsl(${hue + 10}, 70%, ${lightness + 10}%)`);
      gradient.addColorStop(1, `hsl(${hue + 20}, 80%, ${lightness + 15}%)`);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Stars when score is high
      if (game.score > 200) {
        ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
        for (let i = 0; i < 30; i++) {
          const x = (i * 137) % CANVAS_WIDTH;
          const y = (i * 421) % (300 * scaleFactor);
          const twinkle = Math.sin(game.frameCount * 0.08 + i) * 0.6 + 0.4;
          const size = ((i % 3) + 1) * scaleFactor;
          ctx.globalAlpha = twinkle;
          ctx.beginPath();
          ctx.arc(x, y, size, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      }

      // Enhanced clouds
      game.clouds.forEach((cloud) => {
        ctx.save();
        ctx.globalAlpha = cloud.opacity;

        const cloudGradient = ctx.createRadialGradient(
          cloud.x + cloud.size,
          cloud.y,
          0,
          cloud.x + cloud.size,
          cloud.y,
          cloud.size * 1.5
        );
        cloudGradient.addColorStop(0, "rgba(255, 255, 255, 0.9)");
        cloudGradient.addColorStop(0.7, "rgba(255, 255, 255, 0.6)");
        cloudGradient.addColorStop(1, "rgba(255, 255, 255, 0)");

        ctx.fillStyle = cloudGradient;
        ctx.beginPath();
        ctx.arc(cloud.x, cloud.y, cloud.size, 0, Math.PI * 2);
        ctx.arc(
          cloud.x + cloud.size * 0.8,
          cloud.y - cloud.size * 0.2,
          cloud.size * 0.9,
          0,
          Math.PI * 2
        );
        ctx.arc(
          cloud.x + cloud.size * 1.5,
          cloud.y,
          cloud.size * 0.7,
          0,
          Math.PI * 2
        );
        ctx.fill();

        cloud.x -= cloud.speed;
        if (cloud.x < -cloud.size * 2) {
          cloud.x = CANVAS_WIDTH + cloud.size;
          cloud.y = Math.random() * (CANVAS_HEIGHT * 0.5) + 50 * scaleFactor;
        }
        ctx.restore();
      });

      // Distant mountains with layers
      const mountainLayers = [
        { y: CANVAS_HEIGHT * 0.7, opacity: 0.15, color: "#6a5acd" },
        { y: CANVAS_HEIGHT * 0.72, opacity: 0.25, color: "#7b68ee" },
        { y: CANVAS_HEIGHT * 0.74, opacity: 0.35, color: "#8a7fd4" },
      ];

      mountainLayers.forEach((layer) => {
        ctx.fillStyle =
          layer.color +
          Math.floor(layer.opacity * 255)
            .toString(16)
            .padStart(2, "0");
        ctx.beginPath();
        ctx.moveTo(0, layer.y);
        for (let i = 0; i < CANVAS_WIDTH; i += 60 * scaleFactor) {
          const height = Math.sin(i * 0.008 + layer.y) * 40 * scaleFactor;
          ctx.lineTo(i, layer.y - height);
        }
        ctx.lineTo(CANVAS_WIDTH, layer.y);
        ctx.fill();
      });
    }

    function drawUI() {
      // Score with enhanced styling
      ctx.save();
      ctx.textAlign = "left";

      // Score background
      const scorePadding = 15 * scaleFactor;
      const scoreText = `${game.score}`;
      ctx.font = `bold ${42 * scaleFactor}px Arial`;
      const scoreWidth = ctx.measureText(scoreText).width;

      const bgGradient = ctx.createLinearGradient(
        20 * scaleFactor,
        20 * scaleFactor,
        20 * scaleFactor + scoreWidth + scorePadding * 2,
        60 * scaleFactor
      );
      bgGradient.addColorStop(0, "rgba(255, 255, 255, 0.95)");
      bgGradient.addColorStop(1, "rgba(255, 255, 255, 0.85)");
      ctx.fillStyle = bgGradient;
      ctx.shadowBlur = 15 * scaleFactor;
      ctx.shadowColor = "rgba(0, 0, 0, 0.2)";

      roundRect(
        ctx,
        20 * scaleFactor,
        20 * scaleFactor,
        scoreWidth + scorePadding * 2,
        50 * scaleFactor,
        15 * scaleFactor
      );
      ctx.fill();

      // Score text - centered
      ctx.shadowBlur = 3 * scaleFactor;
      ctx.shadowColor = "rgba(102, 126, 234, 0.3)";
      const textGradient = ctx.createLinearGradient(
        0,
        30 * scaleFactor,
        0,
        60 * scaleFactor
      );
      textGradient.addColorStop(0, "#667eea");
      textGradient.addColorStop(1, "#764ba2");
      ctx.fillStyle = textGradient;
      ctx.textAlign = "center";
      ctx.fillText(
        scoreText,
        20 * scaleFactor + (scoreWidth + scorePadding * 2) / 2,
        55 * scaleFactor
      );

      ctx.restore();

      // Player name badge (responsive)
      if (CANVAS_WIDTH > 500) {
        ctx.save();
        ctx.font = `bold ${16 * scaleFactor}px Arial`;
        const nameText = `🤖 ${playerName}`;
        const nameWidth = ctx.measureText(nameText).width;

        ctx.fillStyle = "rgba(118, 75, 162, 0.95)";
        ctx.shadowBlur = 10 * scaleFactor;
        ctx.shadowColor = "rgba(118, 75, 162, 0.5)";
        roundRect(
          ctx,
          CANVAS_WIDTH - nameWidth - 50 * scaleFactor,
          20 * scaleFactor,
          nameWidth + 30 * scaleFactor,
          35 * scaleFactor,
          20 * scaleFactor
        );
        ctx.fill();

        ctx.shadowBlur = 0;
        ctx.fillStyle = "white";
        ctx.textAlign = "center";
        ctx.fillText(
          nameText,
          CANVAS_WIDTH - nameWidth / 2 - 35 * scaleFactor,
          42 * scaleFactor
        );
        ctx.restore();
      }

      // AI Boost indicator
      if (game.aiBoostActive) {
        ctx.save();
        const x = CANVAS_WIDTH / 2;
        const y = 60 * scaleFactor;
        const pulse = Math.sin(game.frameCount * 0.25) * 0.15 + 1;

        ctx.translate(x, y);
        ctx.scale(pulse, pulse);

        ctx.shadowBlur = 30 * scaleFactor;
        ctx.shadowColor = "#ff6b6b";
        ctx.fillStyle = "#ff6b6b";
        ctx.font = `bold ${Math.max(20, 28 * scaleFactor)}px Arial`;
        ctx.textAlign = "center";
        ctx.fillText(
          CANVAS_WIDTH > 500 ? "🚀 TURBO BOOST! 🚀" : "🚀 TURBO! 🚀",
          0,
          0
        );
        ctx.restore();
      }
    }

    function roundRect(ctx, x, y, width, height, radius) {
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + width - radius, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
      ctx.lineTo(x + width, y + height - radius);
      ctx.quadraticCurveTo(
        x + width,
        y + height,
        x + width - radius,
        y + height
      );
      ctx.lineTo(x + radius, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
    }

    function drawParticles() {
      for (let i = game.particles.length - 1; i >= 0; i--) {
        const particle = game.particles[i];
        particle.update();
        particle.draw(ctx);
        if (particle.life <= 0) {
          game.particles.splice(i, 1);
        }
      }
    }

    // Game logic functions
    function createObstacle() {
      const heights = [60, 75, 90];
      const height =
        heights[Math.floor(Math.random() * heights.length)] * scaleFactor;
      const width = 55 * scaleFactor;
      const logoIndex =
        game.logoImages.length > 0
          ? Math.floor(Math.random() * game.logoImages.length)
          : 0;

      return {
        x: CANVAS_WIDTH,
        y: game.groundY - height,
        width: width,
        height: height,
        passed: false,
        logoIndex: logoIndex,
      };
    }

    function createCoin() {
      return {
        x: CANVAS_WIDTH,
        y: Math.random() * (CANVAS_HEIGHT * 0.5) + CANVAS_HEIGHT * 0.2,
        size: 22 * scaleFactor,
        collected: false,
      };
    }

    function createPowerUp() {
      const types = [
        { type: "shield", color: "#FFD700", icon: "🛡" },
        { type: "double", color: "#4CAF50", icon: "2️⃣" },
        { type: "slow", color: "#2196F3", icon: "⏰" },
      ];
      const powerUpType = types[Math.floor(Math.random() * types.length)];

      return {
        x: CANVAS_WIDTH,
        y: Math.random() * (CANVAS_HEIGHT * 0.5) + CANVAS_HEIGHT * 0.2,
        size: 28 * scaleFactor,
        collected: false,
        ...powerUpType,
      };
    }

    function createParticleExplosion(x, y, color, count = 15, type = "normal") {
      for (let i = 0; i < count; i++) {
        game.particles.push(new Particle(x, y, color, type));
      }
    }

    function updatePlayer() {
      game.player.dy += game.player.gravity;
      game.player.y += game.player.dy;

      // Smooth rotation
      game.player.targetRotation = Math.max(
        -0.3,
        Math.min(0.3, game.player.dy * 0.04)
      );
      game.player.rotation +=
        (game.player.targetRotation - game.player.rotation) * 0.15;

      // Squish effect
      if (game.player.y >= game.groundY - game.player.height) {
        game.player.squish = 0.75;
      }
      game.player.squish += (1 - game.player.squish) * 0.12;

      // Ground collision
      if (game.player.y > game.groundY - game.player.height) {
        game.player.y = game.groundY - game.player.height;
        game.player.dy = 0;
        game.player.isJumping = false;
        game.player.rotation = 0;

        createParticleExplosion(
          game.player.x + game.player.width / 2,
          game.player.y + game.player.height,
          "#4CAF50",
          6,
          "circle"
        );
      }

      if (game.player.y < 0) {
        game.player.y = 0;
        game.player.dy = 0;
      }
    }

    function updateObstacles() {
      const currentSpeed = game.slowMotion
        ? game.gameSpeed * 0.5
        : game.aiBoostActive
        ? game.gameSpeed * 1.8
        : game.gameSpeed;

      for (let i = game.obstacles.length - 1; i >= 0; i--) {
        const obs = game.obstacles[i];
        obs.x -= currentSpeed;

        if (obs.x + obs.width < 0) {
          game.obstacles.splice(i, 1);
        } else if (!obs.passed && obs.x + obs.width < game.player.x) {
          obs.passed = true;
          game.combo++;
          const points =
            (game.doublePoints ? 20 : 10) * Math.min(game.combo, 5);
          game.score += points;
          setScore(game.score);
          setCombo(game.combo);
          setShowCombo(true);
          setTimeout(() => setShowCombo(false), 1000);

          createParticleExplosion(
            obs.x + obs.width / 2,
            obs.y + obs.height / 2,
            "#FFD700",
            12,
            "star"
          );
        }
      }
    }

    function updateCoins() {
      const currentSpeed = game.slowMotion
        ? game.gameSpeed * 0.5
        : game.aiBoostActive
        ? game.gameSpeed * 1.8
        : game.gameSpeed;

      for (let i = game.coins.length - 1; i >= 0; i--) {
        const coin = game.coins[i];
        coin.x -= currentSpeed;

        if (
          !coin.collected &&
          game.player.x < coin.x + coin.size &&
          game.player.x + game.player.width > coin.x &&
          game.player.y < coin.y + coin.size &&
          game.player.y + game.player.height > coin.y
        ) {
          coin.collected = true;
          const points = game.doublePoints ? 10 : 5;
          game.score += points;
          setScore(game.score);

          createParticleExplosion(
            coin.x + coin.size / 2,
            coin.y + coin.size / 2,
            "#FFD700",
            15,
            "spark"
          );
          game.coins.splice(i, 1);
        } else if (coin.x + coin.size < 0) {
          game.coins.splice(i, 1);
        }
      }
    }

    function updatePowerUps() {
      const currentSpeed = game.slowMotion
        ? game.gameSpeed * 0.5
        : game.aiBoostActive
        ? game.gameSpeed * 1.8
        : game.gameSpeed;

      for (let i = game.powerUps.length - 1; i >= 0; i--) {
        const powerUp = game.powerUps[i];
        powerUp.x -= currentSpeed;

        if (
          !powerUp.collected &&
          game.player.x < powerUp.x + powerUp.size &&
          game.player.x + game.player.width > powerUp.x &&
          game.player.y < powerUp.y + powerUp.size &&
          game.player.y + game.player.height > powerUp.y
        ) {
          powerUp.collected = true;
          activatePowerUp(powerUp.type);
          createParticleExplosion(
            powerUp.x,
            powerUp.y,
            powerUp.color,
            20,
            "star"
          );
          game.powerUps.splice(i, 1);
        } else if (powerUp.x + powerUp.size < 0) {
          game.powerUps.splice(i, 1);
        }
      }
    }

    function activatePowerUp(type) {
      const powerUpMap = {
        shield: () => {
          game.invincible = true;
          setActivePowerUps((prev) => [
            ...prev.filter((p) => p !== "shield"),
            "shield",
          ]);
          setTimeout(() => {
            game.invincible = false;
            setActivePowerUps((prev) => prev.filter((p) => p !== "shield"));
          }, 5000);
        },
        double: () => {
          game.doublePoints = true;
          setActivePowerUps((prev) => [
            ...prev.filter((p) => p !== "double"),
            "double",
          ]);
          setTimeout(() => {
            game.doublePoints = false;
            setActivePowerUps((prev) => prev.filter((p) => p !== "double"));
          }, 8000);
        },
        slow: () => {
          game.slowMotion = true;
          setActivePowerUps((prev) => [
            ...prev.filter((p) => p !== "slow"),
            "slow",
          ]);
          setTimeout(() => {
            game.slowMotion = false;
            setActivePowerUps((prev) => prev.filter((p) => p !== "slow"));
          }, 4000);
        },
      };
      powerUpMap[type]?.();
    }

    function checkCollision() {
      if (game.invincible) return false;
      const player = game.player;

      for (let obs of game.obstacles) {
        if (
          player.x + 10 * scaleFactor < obs.x + obs.width - 10 * scaleFactor &&
          player.x + player.width - 10 * scaleFactor >
            obs.x + 10 * scaleFactor &&
          player.y + 10 * scaleFactor < obs.y + obs.height - 10 * scaleFactor &&
          player.y + player.height - 10 * scaleFactor > obs.y + 10 * scaleFactor
        ) {
          createParticleExplosion(
            player.x + player.width / 2,
            player.y + player.height / 2,
            "#ff6b6b",
            40,
            "spark"
          );
          return true;
        }
      }
      return false;
    }

    function update() {
      if (!game.running) return;

      game.frameCount++;
      game.backgroundOffset =
        (game.backgroundOffset + game.gameSpeed * 0.1) % CANVAS_WIDTH;

      updatePlayer();

      if (game.frameCount % 85 === 0) {
        game.obstacles.push(createObstacle());
      }
      if (game.frameCount % 110 === 0) {
        game.coins.push(createCoin());
      }
      if (game.frameCount % 280 === 0) {
        game.powerUps.push(createPowerUp());
      }

      updateObstacles();
      updateCoins();
      updatePowerUps();

      if (checkCollision()) {
        endGame();
        return;
      }

      game.gameSpeed = (6 + game.score / 150) * scaleFactor;

      if (!game.player.isJumping && game.combo > 0) {
        setTimeout(() => {
          if (!game.player.isJumping) {
            game.combo = 0;
            setCombo(0);
          }
        }, 2000);
      }
    }

    function draw() {
      drawBackground();
      drawGround();
      game.obstacles.forEach(drawObstacle);
      game.coins.forEach(drawCoin);
      game.powerUps.forEach(drawPowerUp);
      drawPlayer();
      drawParticles();
      drawUI();
    }

    function gameLoop() {
      if (!game.running) return;
      update();
      draw();
      gameLoopRef.current = requestAnimationFrame(gameLoop);
    }

    function jump(isDoubleJump = false) {
      if (
        !game.player.isJumping &&
        game.player.y === game.groundY - game.player.height
      ) {
        game.player.dy = isDoubleJump
          ? game.player.superJumpPower
          : game.player.jumpPower;
        game.player.isJumping = true;
        game.player.squish = 1.3;

        const particleColor = isDoubleJump ? "#FFD700" : "#667eea";
        const particleCount = isDoubleJump ? 20 : 10;
        createParticleExplosion(
          game.player.x + game.player.width / 2,
          game.player.y + game.player.height,
          particleColor,
          particleCount,
          isDoubleJump ? "star" : "circle"
        );
      }
    }

    function activateAIBoost() {
      if (!game.aiBoostCooldown && game.running) {
        game.aiBoostActive = true;
        setAiBoostStatus("ACTIVE");

        createParticleExplosion(
          game.player.x + game.player.width / 2,
          game.player.y + game.player.height / 2,
          "#ff6b6b",
          30,
          "star"
        );

        setTimeout(() => {
          game.aiBoostActive = false;
          game.aiBoostCooldown = true;
          setAiBoostStatus("COOLDOWN");

          setTimeout(() => {
            game.aiBoostCooldown = false;
            setAiBoostStatus("READY");
          }, 3000);
        }, 2000);
      }
    }

    function startGame() {
      game.running = true;
      game.score = 0;
      game.combo = 0;
      game.gameSpeed = 6 * scaleFactor;
      game.obstacles = [];
      game.coins = [];
      game.powerUps = [];
      game.particles = [];
      game.frameCount = 0;
      game.player.y = game.groundY - game.player.height;
      game.player.dy = 0;
      game.player.rotation = 0;
      game.player.isJumping = false;
      game.player.trail = [];
      game.aiBoostActive = false;
      game.aiBoostCooldown = false;
      game.invincible = false;
      game.doublePoints = false;
      game.slowMotion = false;
      game.startTime = Date.now();
      setScore(0);
      setCombo(0);
      setActivePowerUps([]);
      setAiBoostStatus("READY");
      setGameOver(false);
      gameLoop();
    }

    function endGame() {
      game.running = false;
      setGameOver(true);
      const duration = Math.floor((Date.now() - game.startTime) / 1000);

      if (game.score > highScore) {
        setHighScore(game.score);
      }

      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }

      onGameOver({
        score: game.score,
        duration_seconds: duration,
        metadata: {
          obstacles_passed: Math.floor(game.score / 10),
          final_speed: game.gameSpeed.toFixed(2),
          max_combo: game.combo,
        },
      });
    }

    // Event handlers
    let spacePressed = false;
    let spaceHoldTimer = null;

    function handleKeyDown(e) {
      if (e.code === "Space") {
        e.preventDefault();

        if (!game.running) {
          setGameStarted(true);
          startGame();
          return;
        }

        if (!spacePressed) {
          spacePressed = true;
          jump(false);
          spaceHoldTimer = setTimeout(() => activateAIBoost(), 500);
        }
      }

      // W key for super jump
      if (e.code === "KeyW") {
        e.preventDefault();

        if (!game.running) {
          setGameStarted(true);
          startGame();
          return;
        }

        jump(true);
      }
    }

    function handleKeyUp(e) {
      if (e.code === "Space") {
        spacePressed = false;
        if (spaceHoldTimer) {
          clearTimeout(spaceHoldTimer);
          spaceHoldTimer = null;
        }
      }
    }

    function handleClick() {
      if (!game.running) {
        setGameStarted(true);
        startGame();
      } else {
        jump();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    canvas.addEventListener("click", handleClick);

    function drawStartScreen() {
      drawBackground();
      drawGround();

      ctx.save();

      // Title with gradient and shadow
      const titleGradient = ctx.createLinearGradient(
        0,
        CANVAS_HEIGHT / 2 - 100 * scaleFactor,
        0,
        CANVAS_HEIGHT / 2 - 30 * scaleFactor
      );
      titleGradient.addColorStop(0, "#667eea");
      titleGradient.addColorStop(0.5, "#764ba2");
      titleGradient.addColorStop(1, "#f093fb");

      ctx.fillStyle = titleGradient;
      ctx.font = `bold ${Math.max(32, 52 * scaleFactor)}px Arial`;
      ctx.textAlign = "center";
      ctx.shadowBlur = 20 * scaleFactor;
      ctx.shadowColor = "rgba(102, 126, 234, 0.5)";

      const bounce = Math.sin(Date.now() * 0.003) * 15 * scaleFactor;
      ctx.fillText(
        "ROBOT RUSH",
        CANVAS_WIDTH / 2,
        CANVAS_HEIGHT / 2 - 60 * scaleFactor + bounce
      );

      // Subtitle
      ctx.shadowBlur = 10 * scaleFactor;
      ctx.font = `${Math.max(16, 24 * scaleFactor)}px Arial`;
      ctx.fillStyle = "#333";
      ctx.fillText(
        CANVAS_WIDTH > 500 ? "Click or Press SPACE to Start!" : "Tap to Start!",
        CANVAS_WIDTH / 2,
        CANVAS_HEIGHT / 2 + 10 * scaleFactor
      );

      // Instructions
      ctx.font = `${Math.max(14, 18 * scaleFactor)}px Arial`;
      ctx.fillStyle = "#666";
      ctx.shadowBlur = 5 * scaleFactor;
      ctx.fillText(
        CANVAS_WIDTH > 500
          ? "Press W for Super Jump! 🚀"
          : "Super Jump Available! 🚀",
        CANVAS_WIDTH / 2,
        CANVAS_HEIGHT / 2 + 50 * scaleFactor
      );

      if (!game.imagesLoaded) {
        ctx.font = `${Math.max(12, 16 * scaleFactor)}px Arial`;
        ctx.fillStyle = "#764ba2";
        ctx.fillText(
          "Loading assets...",
          CANVAS_WIDTH / 2,
          CANVAS_HEIGHT / 2 + 90 * scaleFactor
        );
      }

      ctx.restore();
    }

    drawStartScreen();
    const startScreenInterval = setInterval(() => {
      if (!game.running) {
        drawStartScreen();
      } else {
        clearInterval(startScreenInterval);
      }
    }, 50);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      canvas.removeEventListener("click", handleClick);
      clearInterval(startScreenInterval);
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
    };
  }, [sessionToken, onGameOver, playerName, highScore, canvasDimensions]);
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e8c3fe] via-[#e8c3fe] to-[#ba8ddb] flex items-center justify-center p-2 sm:p-4">
      <div className="w-full max-w-6xl" ref={containerRef}>
        {/* Header Stats */}
        <div className="bg-white backdrop-blur rounded-t-2xl sm:rounded-t-3xl p-3 sm:p-4 md:p-6 shadow-2xl">
          <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3 md:gap-4">
            {/* Score */}
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="bg-gradient-to-br from-[#e8c3fe] to-[#ba8ddb] text-white rounded-xl sm:rounded-2xl px-3 py-2 sm:px-4 sm:py-2 md:px-6 md:py-3 shadow-lg">
                <div className="text-xs font-bold text-purple-100 mb-1 text-center">
                  SCORE
                </div>
                <div className="text-xl sm:text-2xl md:text-3xl font-black text-center">
                  {score}
                </div>
              </div>
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 text-gray-700 rounded-xl sm:rounded-2xl px-3 py-2 sm:px-4 sm:py-2 md:px-6 md:py-3">
                <div className="text-xs font-bold text-gray-500 mb-1 text-center">
                  BEST
                </div>
                <div className="text-lg sm:text-xl md:text-2xl font-black flex items-center gap-1 justify-center">
                  <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
                  {highScore}
                </div>
              </div>
            </div>

            {/* Combo */}
            {combo > 1 && (
              <div
                className={`bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-xl sm:rounded-2xl px-3 py-2 sm:px-4 sm:py-2 md:px-6 md:py-3 shadow-lg transform transition-all ${
                  showCombo ? "scale-110" : "scale-100"
                }`}
              >
                <div className="text-xs font-bold mb-1 text-center">COMBO</div>
                <div className="text-xl sm:text-2xl md:text-3xl font-black flex items-center gap-1 sm:gap-2 justify-center">
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                  {combo}x
                </div>
              </div>
            )}

            {/* Turbo Status */}
            <div
              className={`rounded-xl sm:rounded-2xl px-3 py-2 sm:px-4 sm:py-2 md:px-6 md:py-3 shadow-lg font-bold transition-all ${
                aiBoostStatus === "READY"
                  ? "bg-gradient-to-r from-pink-500 to-red-500 text-white animate-pulse"
                  : aiBoostStatus === "ACTIVE"
                  ? "bg-gradient-to-r from-red-500 to-orange-600 text-white"
                  : "bg-gray-300 text-gray-600"
              }`}
            >
              <div className="text-xs mb-1 text-center">TURBO</div>
              <div className="text-sm sm:text-base md:text-lg flex items-center gap-1 sm:gap-2 justify-center">
                {aiBoostStatus === "READY" && (
                  <>
                    <Zap className="w-4 h-4 sm:w-5 sm:h-5" /> READY
                  </>
                )}
                {aiBoostStatus === "ACTIVE" && (
                  <>
                    <Zap className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" /> GO!
                  </>
                )}
                {aiBoostStatus === "COOLDOWN" && (
                  <>
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5" /> COOL
                  </>
                )}
              </div>
            </div>

            {/* Player */}
            <div className="bg-gradient-to-r from-[#e8c3fe] to-[#ba8ddb] text-white rounded-xl sm:rounded-2xl px-3 py-2 sm:px-4 sm:py-2 md:px-6 md:py-3 shadow-lg">
              <div className="text-xs font-bold text-purple-200 mb-1 text-center">
                PLAYER
              </div>
              <div className="text-sm sm:text-base md:text-lg font-black truncate max-w-32 sm:max-w-none text-center">
                🤖 {playerName}
              </div>
            </div>
          </div>

          {/* Active Power-ups */}
          {activePowerUps.length > 0 && (
            <div className="mt-3 sm:mt-4 flex flex-wrap gap-2">
              {activePowerUps.includes("shield") && (
                <div className="bg-yellow-400 text-yellow-900 px-3 py-1 sm:px-4 sm:py-2 rounded-full font-bold text-xs sm:text-sm flex items-center gap-1 sm:gap-2 shadow">
                  <Shield className="w-3 h-3 sm:w-4 sm:h-4" /> SHIELD
                </div>
              )}
              {activePowerUps.includes("double") && (
                <div className="bg-green-400 text-green-900 px-3 py-1 sm:px-4 sm:py-2 rounded-full font-bold text-xs sm:text-sm flex items-center gap-1 sm:gap-2 shadow">
                  <Star className="w-3 h-3 sm:w-4 sm:h-4" /> 2X PTS
                </div>
              )}
              {activePowerUps.includes("slow") && (
                <div className="bg-blue-400 text-blue-900 px-3 py-1 sm:px-4 sm:py-2 rounded-full font-bold text-xs sm:text-sm flex items-center gap-1 sm:gap-2 shadow">
                  <Clock className="w-3 h-3 sm:w-4 sm:h-4" /> SLOW-MO
                </div>
              )}
            </div>
          )}
        </div>

        {/* Game Canvas */}
        <div
          className="relative bg-white justify-items"
          style={{ justifyItems: "center" }}
        >
          <canvas ref={canvasRef} className="w-full cursor-pointer block" />
          {gameOver && (
            <div className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 text-center shadow-2xl max-w-sm w-full">
                <div className="text-4xl sm:text-5xl md:text-6xl mb-2 sm:mb-4">
                  💥
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-800 mb-2">
                  Game Over!
                </h2>
                <p className="text-4xl sm:text-5xl md:text-6xl font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-3 sm:mb-4 md:mb-6">
                  {score}
                </p>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  Tap to play again
                </p>
                <div className="text-xs sm:text-sm text-gray-500">
                  {highScore > 0 && score === highScore && (
                    <div className="text-yellow-600 font-bold flex items-center justify-center gap-1 sm:gap-2 mb-2">
                      <Trophy className="w-4 h-4 sm:w-5 sm:h-5" /> NEW HIGH
                      SCORE!
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="bg-white backdrop-blur rounded-b-2xl sm:rounded-b-3xl p-3 sm:p-4 md:p-6 shadow-2xl">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-center">
            <div className="bg-gradient-to-r from-purple-100 to-indigo-100 rounded-xl sm:rounded-2xl p-3 sm:p-4">
              <div className="inline-block bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-3 py-1 sm:px-4 sm:py-2 rounded-lg font-bold text-xs sm:text-sm mb-2">
                SPACE
              </div>
              <p className="text-gray-700 font-medium text-xs sm:text-base">
                Normal Jump
              </p>
            </div>
            <div className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-xl sm:rounded-2xl p-3 sm:p-4">
              <div className="inline-block bg-gradient-to-r from-yellow-600 to-orange-600 text-white px-3 py-1 sm:px-4 sm:py-2 rounded-lg font-bold text-xs sm:text-sm mb-2">
                W KEY
              </div>
              <p className="text-gray-700 font-medium text-xs sm:text-base">
                Super Jump (2x Height)
              </p>
            </div>
            <div className="bg-gradient-to-r from-pink-100 to-red-100 rounded-xl sm:rounded-2xl p-3 sm:p-4">
              <div className="inline-block bg-gradient-to-r from-pink-600 to-red-600 text-white px-3 py-1 sm:px-4 sm:py-2 rounded-lg font-bold text-xs sm:text-sm mb-2">
                HOLD SPACE
              </div>
              <p className="text-gray-700 font-medium text-xs sm:text-base">
                Turbo Boost
              </p>
            </div>
          </div>

          {/* Power-ups Explanation */}
          <div className="mt-4 sm:mt-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl sm:rounded-2xl p-3 sm:p-4">
            <h3 className="text-center font-bold text-gray-800 mb-3 text-sm sm:text-base">
              ✨ Collectibles Guide
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl mb-1">💰</div>
                <p className="text-xs sm:text-sm font-semibold text-gray-700">
                  Coins
                </p>
                <p className="text-xs text-gray-600">+5 Points</p>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl mb-1">🛡</div>
                <p className="text-xs sm:text-sm font-semibold text-gray-700">
                  Shield
                </p>
                <p className="text-xs text-gray-600">Invincibility</p>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl mb-1">2️⃣</div>
                <p className="text-xs sm:text-sm font-semibold text-gray-700">
                  Double
                </p>
                <p className="text-xs text-gray-600">2x Points</p>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl mb-1">⏰</div>
                <p className="text-xs sm:text-sm font-semibold text-gray-700">
                  Slow-Mo
                </p>
                <p className="text-xs text-gray-600">Slower Game</p>
              </div>
            </div>
          </div>

          {!imagesLoaded && (
            <div className="mt-3 sm:mt-4 text-center text-purple-600 font-bold text-xs sm:text-sm animate-pulse">
              ⏳ Loading...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RobotJumpGame;
